/** System / web-safe fonts available without loading a webfont. */
export const SYSTEM_BRAND_FONTS: string[] = [
  'Arial',
  'Helvetica',
  'Helvetica Neue',
  'Georgia',
  'Times New Roman',
  'Times',
  'Courier New',
  'Courier',
  'Verdana',
  'Tahoma',
  'Trebuchet MS',
  'Palatino Linotype',
  'Garamond',
  'Brush Script MT',
  'Impact',
  'system-ui',
  '-apple-system',
  'Segoe UI',
  'Roboto',
];

const GOOGLE_FONTS_CDN =
  'https://cdn.jsdelivr.net/gh/hasinhayder/google-fonts@latest/fonts.json';

let googleFontsCache: string[] | null = null;
let googleFontsPromise: Promise<string[]> | null = null;

/** Full Google Fonts family catalog (cached after first fetch). */
export async function loadGoogleFontFamilies(): Promise<string[]> {
  if (googleFontsCache) return googleFontsCache;
  if (googleFontsPromise) return googleFontsPromise;

  googleFontsPromise = (async () => {
    try {
      const res = await fetch(GOOGLE_FONTS_CDN);
      if (!res.ok) throw new Error(`Failed to load Google Fonts (${res.status})`);
      const data = (await res.json()) as { fonts?: string[] } | string[];
      const fonts = Array.isArray(data) ? data : data.fonts || [];
      googleFontsCache = fonts.filter((f) => typeof f === 'string' && f.trim());
      return googleFontsCache;
    } catch (err) {
      console.error('Failed to load Google Fonts catalog:', err);
      googleFontsPromise = null;
      return [];
    }
  })();

  return googleFontsPromise;
}

const loadedPreviewFonts = new Set<string>();

/** Load a Google Font for live preview in the picker (no-op for system fonts). */
export function ensureFontPreviewLoaded(family: string): void {
  const name = family.trim();
  if (!name || loadedPreviewFonts.has(name)) return;
  if (SYSTEM_BRAND_FONTS.includes(name)) {
    loadedPreviewFonts.add(name);
    return;
  }

  loadedPreviewFonts.add(name);
  const id = `brand-font-preview-${name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;
  if (document.getElementById(id)) return;

  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(name).replace(
    /%20/g,
    '+',
  )}:wght@400;600&display=swap`;
  document.head.appendChild(link);
}

export function fontStack(family: string | null | undefined): string | undefined {
  const name = (family || '').trim();
  if (!name) return undefined;
  return `"${name.replace(/"/g, '')}", system-ui, sans-serif`;
}
